import { gql } from '@apollo/client';
import { CloudinaryTransformations } from '@mnd-frontend/graphql-schema';
import { FormField, Input, useToasts } from '@mnd-frontend/ui';
import { isEqual } from 'lodash-es';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUploader from '../../../components/MediaUploaders/ImageUploader';
import { ACCEPT_IMAGE_FILES } from '../../../components/MediaUploaders/constants';
import { useCurrentPressroom } from '../../../contexts/CurrentPressroomContext';
import {
  type NewsroomDataPressContact,
  useNewsroomSetup,
} from '../../../contexts/NewsroomSetupContext';
import { useCreateContactPersonOnboardingMutation } from '../../../generated/hooks';
import { useCloudinaryImageUpload } from '../../../hooks/useCloudinaryImageUpload';
import { track } from '../../../lib/tracker';
import Footer from './Footer';
import Header from './Header';
import { Form, ImgUploaderWrapper, StepForm, StepSectionWrapper } from './styles';

gql`
  fragment PressContact on Pressroom {
    id
    contactPeopleList(perPage: 1) {
      id
      contactPeople2 {
        id
        name
        title
        email
        phone
        imageUrl
        image {
          id
          url(transformation: CONTACT_PERSON)
        }
        publiclyVisible
        showAsPressroomContact
      }
    }
  }

  mutation createContactPersonOnboarding(
    $pressroomId: ID!
    $id: ID
    $name: String!
    $title: String!
    $email: String!
    $phone: String
    $resourceImageId: ID
  ) {
    contactPersonUpdate(
      input: {
        pressroomId: $pressroomId
        id: $id
        name: $name
        title: $title
        email: $email
        phone: $phone
        resourceImageId: $resourceImageId
        showAsPressroomContact: true
        publiclyVisible: true
      }
    ) {
      pressroom {
        id
        ...PressContact
      }
      error {
        message
        ... on ContactPersonUpdateValidationError {
          name
          email
        }
      }
    }
  }
`;

const PressContact = () => {
  const [t] = useTranslation();
  const { showFailureToast } = useToasts();
  const { pressroomId } = useCurrentPressroom();
  const { newsroomData, handleNewsroomDataFieldChange, setNewsroomSetupStep } = useNewsroomSetup();

  const formRef = useRef<HTMLFormElement>(null);
  const initialValues = useRef<NewsroomDataPressContact>(newsroomData['press-contact']);

  const [uploadImage] = useCloudinaryImageUpload({
    onError: () => showFailureToast(t('common_general_error')),
  });

  const [create, { data, loading }] = useCreateContactPersonOnboardingMutation({
    onCompleted: response => {
      const responseError = response.contactPersonUpdate?.error;
      if (responseError) {
        if (responseError.message) {
          showFailureToast(responseError.message);
        }
      } else {
        const dataChanged = !isEqual(initialValues.current, newsroomData['press-contact']);
        track({
          event: 'Newsroom Setup: Press Contact - Proceed',
          traits: {
            step: 2,
            dataChanged,
            fieldTitlePopulated: !!newsroomData['press-contact'].title,
            fieldEmailPopulated: !!newsroomData['press-contact'].email,
            fieldNumberPopulated: !!newsroomData['press-contact'].phone,
            fieldPhotoPopulated: !!newsroomData['press-contact'].imageUrl,
          },
        });
        setNewsroomSetupStep('company-description');
      }
    },
    onError: () => showFailureToast(t('common_general_error')),
    variables: {
      pressroomId,
      id: newsroomData['press-contact'].id,
      name: newsroomData['press-contact'].name,
      title: newsroomData['press-contact'].title,
      email: newsroomData['press-contact'].email,
      phone: newsroomData['press-contact'].phone,
      resourceImageId: newsroomData['press-contact'].imageResourceId,
    },
  });

  const validationError =
    data?.contactPersonUpdate?.error?.__typename === 'ContactPersonUpdateValidationError'
      ? data.contactPersonUpdate.error
      : undefined;

  useEffect(() => {
    if (validationError || formRef.current) {
      formRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [validationError]);

  useEffect(() => {
    const prepopulated = Boolean(
      newsroomData['press-contact'].name && newsroomData['press-contact'].email,
    );
    track({ event: 'Newsroom Setup: Press Contact', traits: { step: 2, prepopulated } });
  }, []);

  return (
    <StepForm
      ref={formRef}
      onSubmit={e => {
        e.preventDefault();
        create();
      }}
    >
      <StepSectionWrapper>
        <Header currentStep={2} heading={t('onboarding-press-contact_title')} />
        <Form>
          <Input
            error={validationError?.name}
            label={t('newsroom_setup_wizard.contact_person_form.name_label')}
            id="name"
            value={newsroomData['press-contact'].name}
            onChange={e => handleNewsroomDataFieldChange('press-contact', 'name', e.target.value)}
          />
          <Input
            label={t('newsroom_setup_wizard.contact_person_form.title_label')}
            value={newsroomData['press-contact'].title}
            onChange={e => handleNewsroomDataFieldChange('press-contact', 'title', e.target.value)}
          />
          <Input
            error={validationError?.email}
            label={t('newsroom_setup_wizard.contact_person_form.email_label')}
            id="email"
            value={newsroomData['press-contact'].email}
            onChange={e => handleNewsroomDataFieldChange('press-contact', 'email', e.target.value)}
          />
          <Input
            label={t('contacts_details_phone-placeholder')}
            value={newsroomData['press-contact'].phone}
            onChange={e => handleNewsroomDataFieldChange('press-contact', 'phone', e.target.value)}
          />
          <FormField
            label={t('newsroom_setup_wizard.contact_person_form.image_label')}
            id="file-upload"
          >
            <ImgUploaderWrapper>
              <ImageUploader
                imageUrl={newsroomData['press-contact'].imageUrl}
                action={async (file: File) => {
                  const resp = await uploadImage(file, CloudinaryTransformations.ContactPerson);
                  handleNewsroomDataFieldChange(
                    'press-contact',
                    'imageResourceId',
                    resp?.imageSyncCloudinaryUpload?.image?.id ?? null,
                  );
                  handleNewsroomDataFieldChange(
                    'press-contact',
                    'imageUrl',
                    resp?.imageSyncCloudinaryUpload?.image?.url ?? '',
                  );
                }}
                deleteCallback={() =>
                  handleNewsroomDataFieldChange('press-contact', 'imageUrl', null)
                }
                validatorProps={{
                  validFileFormats: ACCEPT_IMAGE_FILES,
                }}
              />
            </ImgUploaderWrapper>
          </FormField>
        </Form>
        <Footer
          isSaving={loading}
          previousStep="branding"
          nextStep="company-description"
          canContinue={
            !!(
              newsroomData['press-contact'].email ||
              newsroomData['press-contact'].title ||
              newsroomData['press-contact'].name ||
              newsroomData['press-contact'].phone
            )
          }
        />
      </StepSectionWrapper>
    </StepForm>
  );
};

export default PressContact;
